// react
import React, { useMemo } from 'react';
import styled from 'styled-components';

// components
import { ArticleCard, ArticleFeatured } from '@/components/atoms';
import { CardContainer } from '@/components/templates';

// store
import { Article } from '@karehero/models';

// hooks/
import { useWindowSize } from '@/hooks/';

// theme
import theme from '@/theme';

/**
 * ArticleFilterd shows a featured article and a list of articles.
 */
export const ArticleFiltered = ({
  articles,
  bookmarkedArticles,
  isLoading,
  onBookmark,
}: ArticleFilteredProps) => {
  // hooks/
  const [width] = useWindowSize();

  // memos
  const maxColumns = useMemo(() => {
    if (width < theme.breakpoint.mdValue) return 1;
    if (width < theme.breakpoint.lgValue) return 2;
    if (width < theme.breakpoint.xlValue) return 3;
    return 4;
  }, [width]);

  if (!articles || articles.length === 0) return <div>No articles found</div>;

  return (
    <Wrapper>
      <ArticleFeatured
        article={articles?.[0]}
        onBookmark={onBookmark}
        isBookmarked={
          !!bookmarkedArticles?.find((a) => a?._id === articles?.[0]?._id)
        }
        isLoading={isLoading}
      />
      <CardContainer maxColumns={maxColumns}>
        {articles?.slice(1).map((article, i) => (
          <ArticleCard
            key={i}
            article={article}
            isBookmarked={
              !!bookmarkedArticles?.find((a) => a?._id === article?._id)
            }
            onBookmark={onBookmark}
            isLoading={isLoading}
          />
        ))}
      </CardContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${(props) => props.theme.spacing[32]};
`;

interface ArticleFilteredProps {
  articles?: Partial<Article>[];
  bookmarkedArticles?: Article[];
  isLoading: boolean;
  onBookmark?: (article: Partial<Article>) => void;
}

ArticleFiltered.displayName = 'ArticleFiltered';

export default ArticleFiltered;
